// Shared map PIXI scene singleton — extracted from MapCanvas.tsx (decomposition
// step 05a). Lives in its own module so scene hooks (useMapScene) can read/write
// it without a back-import cycle to the MapCanvas god-component.
import * as PIXI from 'pixi.js';
import type { Viewport } from 'pixi-viewport';

export type MapSceneSingleton = {
  app: PIXI.Application | null;
  viewport: Viewport | null;
  mapObjectsLayer: PIXI.Container | null;
  highlightLayer: PIXI.Container | null;
  cloudsLayer: PIXI.Container | null;
  cloudsSprite: PIXI.Sprite | null;
  plotMarkerTexture: PIXI.Texture | null;
  /**
   * In-flight load of plot.webp. On unmount the texture is freed and
   * `plotMarkerTexture` is nulled, but the map-objects render effect can run again
   * before the reload resolves — it used to see null and fall back to a green
   * debug circle, which is what players saw after leaving the map and coming back.
   * Markers created during that window await this and swap in the real texture.
   */
  plotMarkerTexturePromise: Promise<PIXI.Texture> | null;
  newIslandMarker: PIXI.Container | null;
  mapObjectHighlight: PIXI.Container | null;
  plotMarkers: Map<number, PIXI.Container>;
  realEstateMarkers: Map<number, PIXI.Container>;
  mapObjectMarkers: Map<number, PIXI.Container>;
  questMarkers: Map<number, PIXI.Container>;
  islandLayer: PIXI.Container | null;
  islandMarkers: Map<number, PIXI.Container>;
  updateViewportSize: (() => void) | null;
  initPromise: Promise<void> | null;
  isReady: boolean;
  isResizeListenerAttached: boolean;
  stageEventMode: PIXI.EventMode | null;
  /**
   * Render key of the last fully-built marker set. Persisted across mounts so
   * that returning from a room interior (where MapCanvas unmounts but markers
   * are kept alive in the singleton) can skip the full destroy+rebuild when the
   * underlying map data hasn't changed.
   */
  lastMapObjectsRenderKey: string | null;
};

export const mapSceneSingleton: MapSceneSingleton = {
  app: null,
  viewport: null,
  mapObjectsLayer: null,
  highlightLayer: null,
  cloudsLayer: null,
  cloudsSprite: null,
  plotMarkerTexture: null,
  plotMarkerTexturePromise: null,
  newIslandMarker: null,
  mapObjectHighlight: null,
  plotMarkers: new Map(),
  realEstateMarkers: new Map(),
  mapObjectMarkers: new Map(),
  questMarkers: new Map(),
  islandLayer: null,
  islandMarkers: new Map(),
  updateViewportSize: null,
  initPromise: null,
  isReady: false,
  isResizeListenerAttached: false,
  stageEventMode: null,
  lastMapObjectsRenderKey: null,
};

// Dev-only handle so the live scene can be inspected from the browser console or an
// automated probe (tests/e2e/plot-texture-probe.mjs, tools/profiling). Guarded by
// import.meta.env.DEV — or a profiling build (VITE_SCENE_PROBE=1) — so it is
// stripped from production builds.
if (import.meta.env.DEV || import.meta.env.VITE_SCENE_PROBE) {
  (globalThis as unknown as { __mapScene?: MapSceneSingleton }).__mapScene = mapSceneSingleton;
}

/** Module-level mutators — keep PIXI singleton writes out of React component bodies
 *  so react-hooks/immutability does not flag them (same data, not React state). */
export function setLastMapObjectsRenderKey(key: string | null): void {
  mapSceneSingleton.lastMapObjectsRenderKey = key;
}

export function clearMapObjectHighlightSingleton(): void {
  mapSceneSingleton.mapObjectHighlight = null;
}

/** Single source of truth for the free-plot marker asset path. */
export const PLOT_MARKER_TEXTURE_PATH = '/game-assets/map-objects/plot.webp';

/**
 * Resolve the free-plot marker texture, loading it if the last unmount freed it.
 *
 * 🔴 This exists so a plot marker can NEVER be built without its texture.
 * Leaving the map frees map textures (GPU memory, iOS Safari) and nulls the
 * singleton refs, but `isPixiAppReady` is a one-way latch that is never reset to
 * false, and one init branch marks the scene ready without reloading anything.
 * The marker builder therefore used to observe a null texture and fall back to a
 * green debug circle, which players saw as "the free plot turned into a green dot".
 *
 * Healing that race after the fact is not enough — `createPlotIcon` now demands a
 * non-nullable Texture, so the compiler rejects any caller that has not gone
 * through here. Idempotent: concurrent callers share one in-flight load.
 */
export function ensurePlotMarkerTexture(): Promise<PIXI.Texture> {
  const cached = mapSceneSingleton.plotMarkerTexture;
  if (cached && !cached.destroyed) return Promise.resolve(cached);

  if (!mapSceneSingleton.plotMarkerTexturePromise) {
    mapSceneSingleton.plotMarkerTexturePromise = PIXI.Assets
      .load(PLOT_MARKER_TEXTURE_PATH)
      .then((texture: PIXI.Texture) => {
        if (texture?.source) {
          texture.source.autoGenerateMipmaps = true;
          texture.source.scaleMode = 'linear';
        }
        mapSceneSingleton.plotMarkerTexture = texture;
        return texture;
      })
      .catch((e: unknown) => {
        // Clear the memo so a later mount retries instead of caching the failure.
        mapSceneSingleton.plotMarkerTexturePromise = null;
        throw e;
      });
  }
  return mapSceneSingleton.plotMarkerTexturePromise;
}
